import PropTypes from 'prop-types';
import React from 'react';
import {isRetina} from 'utilities/userAgent';
import isEqual from 'lodash/isEqual';
import {fetchChart} from 'state/card/cardService';
import './ChartCard.css';
import {connect} from 'react-redux';
import {loadFeatures} from 'state/feature/featureActions';
import {
  getEnvironment,
  selectIsDavinci,
} from 'state/environment/environmentSelectors';
import {load as loadEnvironment} from 'state/environment/environmentActions';
import * as ErrorStrategy from 'components/VisualizationStrategies/Error/ErrorStrategy';
import constantObject from 'utilities/constantObject';
import {cancelRequest} from 'utilities/Request';
import {selectIsFeatureEnabled} from 'state/feature/featureSelectors';
import {FEATURE_SWITCHES} from 'components/FeatureSwitch';

const STATUSES = constantObject(['IDLE', 'LOADING', 'LOADED', 'ERROR']);

export const SIZES = {
  DETAILS: 'details',
  MEDIUM: 'medium',
};

export const PARTS = {
  ANNOTATIONS: 'annotations',
  DATA: 'data',
  DATA_SOURCE: 'dataSource',
  DATE_INFO: 'dateInfo',
  GRAPH: 'graph',
  IMAGE: 'image',
  PDP: 'pdp',
  SUMMARY: 'summary',
};

const phoenixRender = optionsFn => WrappedComponent => {
  class PhoenixRenderWrapper extends React.Component {
    constructor() {
      super(...arguments);
      this.state = {
        status: STATUSES.IDLE,
      };
    }

    componentDidUpdate(previousProps) {
      const prevOptions = optionsFn(previousProps);
      const options = optionsFn(this.props);

      if (
        previousProps.cardId !== this.props.cardId ||
        !isEqual(prevOptions, options)
      ) {
        this.fetch(this.props, options);
      }
    }

    componentDidMount() {
      this.props.loadFeatures();
      this.props.loadEnvironment();
      this.fetch(this.props, optionsFn(this.props));
    }

    componentWillUnmount() {
      this.cancelFetch();
    }

    cancelFetch() {
      cancelRequest(this.request);
    }

    fetch(props, options) {
      if (options.isResizing) return null;

      const scale = options.scale || (isRetina ? 2 : 1);

      const {
        width,
        height,
        filters,
        functionOverrides,
        filterGroupIds,
        dataControlFilters,
        dateRangeFilter,
        dateTimeElement,
        pageLayout,
      } = options;

      const parts = props.isExternal
        ? options.parts.filter(p => p !== PARTS.PDP)
        : options.parts;

      const renderSize = {width: width * scale, height: height * scale, scale};

      this.setState({
        status: STATUSES.LOADING,
      });

      this.request = fetchChart(props.cardId, {
        mobile: true,
        filters,
        functionOverrides,
        dataControlContext: {filterGroupIds, filters: dataControlFilters},
        dateRangeFilter,
        dateTimeElement,
        ...renderSize,
        parts,
        pageLayout,
        isAddFilterTypeEnabled: props.isAddFilterTypeEnabled,
      });

      this.request.then(
        response => {
          this.setState({
            status: STATUSES.LOADED,
            chartRender: response,
          });
        },
        e => {
          if (props.isDavinci)
            console.error(
              'Error: ' + e.statusText + ' ' + e.status + ' ' + e.message
            ); //to be picked up by puppeteer and fail image
          if (e.statusText === 'abort') return;
          this.setState({
            status: STATUSES.ERROR,
          });
        }
      );
    }

    render() {
      const Error = ErrorStrategy[this.props.size];
      return this.state.status === STATUSES.ERROR ? (
        <Error {...this.props} />
      ) : (
        <WrappedComponent
          isLoaded={this.state.status === STATUSES.LOADED}
          chartRender={this.state.chartRender}
          {...this.props}
        />
      );
    }
  }

  PhoenixRenderWrapper.propTypes = {
    cardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  };

  return connect(
    state => ({
      isExternal: getEnvironment(state).isSaaSaas,
      isAddFilterTypeEnabled: selectIsFeatureEnabled(
        state,
        FEATURE_SWITCHES.ADD_FILTER_TYPE
      ),
      isDavinci: selectIsDavinci(state),
    }),
    {
      loadFeatures,
      loadEnvironment,
    }
  )(PhoenixRenderWrapper);
};

export default phoenixRender;
